/* eslint-disable @typescript-eslint/no-deprecated */
/**
 * React checkbox cell renderer inspired by ag grid cell renderer.
 * @see https://github.com/ag-grid/ag-grid/blob/latest/packages/ag-grid-community/src/rendering/cellRenderers/checkboxCellRenderer.ts
 */
import type { ICellRendererParams } from 'ag-grid-community';
import type { ChangeEvent } from 'react';
import { memo, useCallback, useEffect, useMemo, useState } from 'react';

import { GROUP_AUTO_COLUMN_ID } from 'ag-grid-community';
import { twMerge } from 'tailwind-merge';

import type { CorrectAny } from '@org/types';

import { CheckboxInput } from '../CheckboxInput';

export interface CheckboxCellRendererParams<
  TData = CorrectAny,
  TContext = CorrectAny,
> extends ICellRendererParams<TData, boolean, TContext> {
  /** Set to `true` for the input to be disabled. */
  disabled?: boolean | ((params: ICellRendererParams<TData, boolean, TContext>) => boolean);
  indeterminate?: boolean;
  groupEdit?: boolean;
  label?: string;
  /**
   * On edit callback.
   * @deprecated
   * @param value
   * @returns
   */
  onEdit?: (value: boolean) => void;
}

const CheckboxCellRendererMemo = (props: CheckboxCellRendererParams) => {
  const {
    onEdit,
    label,
    // indeterminate,
    disabled = false,
    groupEdit = false,
    value,
    node,
    column,
    api,
  } = props;
  const [internalValue, setInternalValue] = useState<boolean | undefined>(value ?? undefined);

  const isDisabled = useMemo(
    () => (typeof disabled === 'function' ? disabled(props) : disabled),
    [disabled, props],
  );

  useEffect(() => {
    let isSelected: boolean | undefined;

    if (node.group && column) {
      if (typeof value === 'boolean') {
        isSelected = value;
      } else {
        const colId = column.getColId();
        if (colId.startsWith(GROUP_AUTO_COLUMN_ID)) {
          // if we're grouping by this column, then the value is a string, and we need to parse it
          isSelected =
            value == null || (value as CorrectAny) === ''
              ? undefined
              : (value as CorrectAny) === 'true';
        } else if (node.aggData?.[colId] !== undefined) {
          // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
          isSelected = value ?? undefined;
        }
      }
    } else {
      isSelected = value ?? undefined;
    }
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setInternalValue(isSelected);
  }, [column, node.aggData, node.group, value]);

  const handleChange = useCallback(
    ({ currentTarget: { checked } }: ChangeEvent<HTMLInputElement>) => {
      const sharedEventParams = {
        column: column!,
        colDef: column!.getColDef(),
        data: node.data,
        node,
        rowIndex: node.rowIndex,
        rowPinned: node.rowPinned,
        value,
      };
      api.dispatchEvent({
        type: 'cellEditingStarted',
        ...sharedEventParams,
      });
      const valueChanged = node.setDataValue(column!, checked, 'edit');

      if (valueChanged && groupEdit) {
        node.allLeafChildren?.forEach((childNode) => childNode.setDataValue(column!, checked));
      }

      api.dispatchEvent({
        type: 'cellEditingStopped',
        ...sharedEventParams,
        // @ts-expect-error -- wrong typing
        oldValue: value,
        newValue: checked,
        valueChanged,
      });

      let parent = node.parent;
      while (parent) {
        api.refreshCells({ columns: [column!], rowNodes: [parent], force: true });
        parent = parent.parent;
      }

      if (valueChanged) {
        onEdit?.(checked);
        setInternalValue(checked);
      } else {
        setInternalValue(value ?? undefined);
        api.refreshCells({ columns: [column!], rowNodes: [node], force: true });
      }
    },
    [api, column, groupEdit, node, onEdit, value],
  );

  const groupAggregatedStateFunc = () => {
    if (!node.group) {
      return { checked: false, indeterminate: false };
    }

    const colId = column?.getColId() ?? '';

    const collectChildValues = (
      children: CorrectAny[] | undefined | null,
    ): (boolean | undefined)[] => {
      if (!children) {
        return [];
      }
      const values: (boolean | undefined)[] = [];
      for (const child of children) {
        if (child?.group) {
          values.push(...collectChildValues(child.childrenAfterGroup));
        } else {
          values.push(child?.data?.[colId]);
        }
      }
      return values;
    };

    const childValues = collectChildValues(node.childrenAfterGroup);

    const allChecked = childValues.every((valueTrue) => valueTrue === true);
    const allUnchecked = childValues.every((valueFalse) => valueFalse === false);

    if (childValues.length === 0) {
      return { checked: false, indeterminate: false };
    } else if (allChecked) {
      return { checked: true, indeterminate: false };
    } else if (allUnchecked) {
      return { checked: false, indeterminate: false };
    } else {
      return { checked: false, indeterminate: true };
    }
  };

  const handleGroupOnChange = useCallback(
    ({ currentTarget: { checked } }: ChangeEvent<HTMLInputElement>) => {
      node.allLeafChildren?.forEach((childNode) =>
        childNode.setDataValue(column!, checked, 'group-child'),
      );
      const changed = node.setDataValue(column!, checked, 'edit');
      api.refreshCells({ columns: [column!], force: true });
      // TODO: remove this - this seems to be a bug in ag-grid, sometimes the checkbox is not updated
      if (!changed) {
        node.setDataValue(column!, !checked, 'edit');
      }
    },
    [api, column, node],
  );

  const groupAggregatedState = groupAggregatedStateFunc();

  return (
    <div className="flex h-full items-center justify-center">
      {node.group ? (
        <CheckboxInput
          checked={groupAggregatedState.checked}
          data-testid="CheckboxRenderer"
          disabled={isDisabled}
          indeterminate={groupAggregatedState.indeterminate}
          onChange={handleGroupOnChange}
        >
          {label ? (
            <span className={twMerge('pl-1', isDisabled && 'pointer-events-none opacity-40')}>
              {label}
            </span>
          ) : (
            ''
          )}
        </CheckboxInput>
      ) : null}

      {!node.group && (
        <CheckboxInput
          checked={internalValue ?? false}
          data-testid="CheckboxRenderer"
          disabled={isDisabled}
          onChange={handleChange}
        >
          {label ? (
            <span className={twMerge('pl-1', isDisabled && 'pointer-events-none opacity-40')}>
              {label}
            </span>
          ) : (
            ''
          )}
        </CheckboxInput>
      )}
    </div>
  );
};

export const CheckboxCellRenderer = memo(CheckboxCellRendererMemo);
